// =============================================================================
// Stories page (tenant-admin scope)
//
// Operator-side view of every journal published under this tenant's
// brand. Phase-1 shipped the read + single-row moderate; Phase-2 adds
// the operator workflow features:
//
//   • Audit log + author email — now goes through the
//     `moderate-tenant-journal` Platform edge fn (orchestrator) instead
//     of the direct App call. Every hide/unhide writes a row to
//     platform_audit_log, and hides email the author via Resend when
//     RESEND_API_KEY is set on the edge fn.
//
//   • Sign in as this user — per-row "view as them" button. Reuses the
//     existing `startImpersonation` plumbing (Tenant Impersonation
//     project) so the operator can see exactly what the author sees in
//     a single click.
//
//   • Sort + bulk moderate — sortable Date / Likes / Comments columns
//     and a checkbox column with a "Hide N" batch action. Bulk hide is
//     sequential (not Promise.all) so one failure doesn't poison the
//     run; we surface a brief summary after.
//
//   • KPI tile feeds the tenant Dashboard from the same data — when the
//     page is mounted the Dashboard re-uses fetchTenantJournals.
//
// Visual tokens: Platform uses surface-*/brand-* (NOT shadcn-style
// bg-card/text-foreground). Keep all new chrome on that palette.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Search, RefreshCw, Heart, MessageCircle, Eye, EyeOff,
  Play, ExternalLink, X, MapPin, ChevronUp, ChevronDown, LogIn,
  ArrowUpDown, Flag, AlertTriangle,
} from 'lucide-react';
import { StatCard, Spinner } from '@/components/shared/UIComponents';
import {
  fetchTenantJournals,
  fetchTenantReports,
  fetchJournalReports,
  dismissJournalReports,
  moderateTenantJournal,
  startImpersonation,
} from '@/lib/api';
import type {
  AppTenantJournal,
  TenantReportSummary,
  TenantReportDetail,
} from '@/lib/api';
import type { Tenant } from '@/types';
import {
  MODERATION_REASONS,
  reasonMeta,
  type ModerationReasonCode,
} from '@/lib/moderationCodes';
import FlaggedContentPanel from './FlaggedContentPanel';

/** Auto-flag threshold from Phase 3A spec — 3+ OPEN reports on a single
 *  journal show the red "Needs review" border on the row. Kept as a
 *  module-level constant so the filter dropdown and the row renderer
 *  agree without a prop drill. */
const NEEDS_REVIEW_THRESHOLD = 3;

type StatusFilter = 'all' | 'published' | 'draft' | 'hidden' | 'reported';

// Sortable columns. "date" is published_at when present, else created_at
// so drafts sort by when they were drafted (which is what the operator
// wants to see — "the newest activity, in any column").
type SortKey = 'date' | 'likes' | 'comments';
type SortDir = 'asc' | 'desc';

// Visibility derivation — single source of truth so the chip + the
// stat row + the filter all classify rows the same way.
function statusOf(j: AppTenantJournal): Exclude<StatusFilter, 'all'> {
  if (!j.published_at)     return 'draft';
  if (j.tenant_hidden_at)  return 'hidden';
  return 'published';
}

// "Live N days" — whole days between publish and now. Used per-row
// to give the operator a sense of how stale a story is on the public
// feed without having to mentally subtract dates.
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / 86_400_000);
}

// Short display date — "Apr 15". We keep it locale-aware via
// toLocaleDateString but pin the format to month-short + day so the
// table column doesn't widen when a long month name comes through.
function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Was the author's last edit meaningfully later than the publish
// timestamp? We only surface "edited" when the gap is > 2 days,
// otherwise typical post-publish polish (and especially our own
// hide/unhide actions which also touch updated_at via the trigger)
// would look like author edits and pollute the listing.
function meaningfullyEdited(j: AppTenantJournal): boolean {
  if (!j.published_at || !j.updated_at) return false;
  const gapMs = new Date(j.updated_at).getTime() - new Date(j.published_at).getTime();
  return gapMs > 2 * 86_400_000;
}

// Sort key extractor — published_at → created_at fallback so drafts
// don't all bunch at the tail with NaN sort keys.
function sortValue(j: AppTenantJournal, key: SortKey): number {
  if (key === 'likes')    return j.feed_likes_count    ?? 0;
  if (key === 'comments') return j.feed_comments_count ?? 0;
  const iso = j.published_at || j.created_at;
  const t   = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function StoriesPage({ tenant }: { tenant: Tenant }) {
  const [journals, setJournals] = useState<AppTenantJournal[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState<StatusFilter>('all');

  // Per-row pending state — keyed by journal id — so the Hide/Unhide
  // button on a single row can show a spinner without freezing the
  // rest of the table.
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  // Selected ids for the bulk moderation actions. Both published AND
  // hidden rows are selectable — published rows feed "Hide N", hidden
  // rows feed "Unhide N". Drafts stay unselectable because there's no
  // moderation action to take on them. We track ids as a Set so toggling
  // stays O(1) and we never have to worry about ordering between the
  // set and the rendered list.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bulk-action mode for the confirmation modal. When the operator clicks
  // "Hide N" or "Unhide N" we stash the intent here; confirmHide reads it
  // to decide which side effect to invoke on each selected id.
  const [bulkAction, setBulkAction] = useState<'hide' | 'unhide' | null>(null);

  // Sort state. Default = newest first by date; users can flip it.
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Hide-reason prompt state. Null = closed. When set to a single
  // journal it's the single-row hide modal; when set to 'bulk' the
  // modal applies to every currently-selected published row.
  const [hideTarget, setHideTarget] = useState<AppTenantJournal | 'bulk' | null>(null);
  // Phase 3C — split the freeform reason into a canonical code + notes.
  // Defaults to 'spam' so the dropdown always has a selection (the most
  // common case operators trigger on).
  const [hideReasonCode, setHideReasonCode] = useState<ModerationReasonCode>('spam');
  const [hideNotes,      setHideNotes]      = useState('');

  // Reason-code filter — when set, only hidden rows with matching code
  // show. NULL filter (default) means "all" within the current status
  // filter. We keep this as separate state from the status filter
  // because they compose: e.g. status=hidden + code=spam shows the
  // most common moderation case to dig into.
  const [reasonFilter, setReasonFilter] = useState<ModerationReasonCode | null>(null);

  // ── Phase 3A — reports state ────────────────────────────────────────
  // Per-journal aggregated report summary, keyed by journal_id for O(1)
  // badge lookup during row render. Independent fetch from the
  // journals list so the operator can refresh just reports (which
  // change minute-by-minute as readers file them) without re-pulling
  // the whole journals payload.
  const [reportsByJournal, setReportsByJournal] = useState<Map<string, TenantReportSummary>>(new Map());

  // Reports modal target. Null = closed; otherwise the journal whose
  // reports we're viewing.
  const [reportsModalJournal, setReportsModalJournal] = useState<AppTenantJournal | null>(null);

  // Sign-in-as button state — track per-row so multiple rows can be
  // in different states (the impersonation fn does a network round trip
  // so we want a spinner before the new tab opens).
  const [signingInId, setSigningInId] = useState<string | null>(null);

  // ── Load on mount + on demand ───────────────────────────────────────
  // journals + reports fan out in parallel so the operator's refresh
  // click pulls both. Reports failure is non-fatal — the page still
  // renders without the badges if the App-side fn is down.
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, reports] = await Promise.all([
        fetchTenantJournals(tenant.id),
        fetchTenantReports(tenant.id).catch(() => [] as TenantReportSummary[]),
      ]);
      setJournals(data);
      const map = new Map<string, TenantReportSummary>();
      for (const r of reports) map.set(r.journal_id, r);
      setReportsByJournal(map);
      // Clear any selection that no longer matches a visible row — the
      // selected ids could be stale after a refresh (e.g. operator hid
      // a story in another tab and we re-fetched).
      setSelectedIds(prev => {
        const ids = new Set(data.map(d => d.id));
        const next = new Set<string>();
        for (const id of prev) if (ids.has(id)) next.add(id);
        return next;
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load journals');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenant.id]);

  // ── Stat row ────────────────────────────────────────────────────────
  // All five counts share one pass over journals so the page stays
  // O(n) even if the tenant has thousands of rows. Phase 3A adds a
  // `reported` count drawn from the reportsByJournal map — the
  // operator-facing total number of unique journals with open reports
  // (not the total report count, which would inflate when a story
  // gets multiple reports).
  const stats = useMemo(() => {
    let total = 0, published = 0, draft = 0, hidden = 0, likes = 0;
    for (const j of journals) {
      total++;
      likes += j.feed_likes_count;
      const s = statusOf(j);
      if (s === 'published') published++;
      else if (s === 'draft') draft++;
      else hidden++;
    }
    return { total, published, draft, hidden, likes, reported: reportsByJournal.size };
  }, [journals, reportsByJournal]);

  // ── Author signals (Phase 3A) ──────────────────────────────────────
  // Compute hidden / published counts per author so the row can render
  // a small "3 hidden / 12 published" pill in the Author column.
  // Operators asked for this so repeat-offender patterns jump out at
  // a glance without having to filter by author manually.
  const authorStats = useMemo(() => {
    const map = new Map<string, { hidden: number; published: number }>();
    for (const j of journals) {
      const key = j.user_id;
      if (!key) continue;
      let s = map.get(key);
      if (!s) { s = { hidden: 0, published: 0 }; map.set(key, s); }
      if (j.tenant_hidden_at) s.hidden++;
      else if (j.published_at) s.published++;
    }
    return map;
  }, [journals]);

  // ── Filtered + searched + sorted view ───────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = journals.filter(j => {
      // Phase 3A — "reported" filter is orthogonal to status. A
      // reported story may still be Published, Draft, or Hidden;
      // selecting "Reported" surfaces all rows with ≥1 OPEN report.
      if (filter === 'reported') {
        if (!reportsByJournal.has(j.id)) return false;
      } else if (filter !== 'all' && statusOf(j) !== filter) {
        return false;
      }
      // Reason-code filter — only meaningful on hidden rows because
      // published/draft rows have no reason. We treat the filter as
      // "match this code" so flipping it off (null) restores the row.
      if (reasonFilter && j.tenant_hidden_reason_code !== reasonFilter) return false;
      if (!q) return true;
      const haystack = [
        j.title || '',
        j.author?.display_name || '',
        j.author?.email || '',
        ...(j.destinations || []),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
    // Sort is stable on (sortKey, sortDir). We sort on a COPY because
    // useMemo's input arrays mustn't be mutated.
    const sorted = filtered.slice().sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [journals, filter, search, sortKey, sortDir, reasonFilter, reportsByJournal]);

  // Bulk-select helpers. Any non-draft row is selectable — published
  // rows feed Hide, hidden rows feed Unhide. Drafts stay out because
  // there's no public-feed visibility to toggle on them.
  const selectableIds = useMemo(
    () => visible.filter(j => statusOf(j) !== 'draft').map(j => j.id),
    [visible],
  );
  const allVisibleSelected =
    selectableIds.length > 0 &&
    selectableIds.every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of selectableIds) next.delete(id);
      } else {
        for (const id of selectableIds) next.add(id);
      }
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Split the selection by current status so the bar can offer a
  // contextual action — published rows are the candidates for Hide,
  // hidden rows are the candidates for Unhide. A mixed selection shows
  // both buttons so the operator doesn't have to manually un-select
  // half their rows just to use one verb at a time.
  const selectedByStatus = useMemo(() => {
    const published: string[] = [];
    const hidden:    string[] = [];
    for (const j of journals) {
      if (!selectedIds.has(j.id)) continue;
      const s = statusOf(j);
      if (s === 'published') published.push(j.id);
      else if (s === 'hidden') hidden.push(j.id);
    }
    return { published, hidden };
  }, [journals, selectedIds]);

  // ── Sort header click handler ───────────────────────────────────────
  // Clicking the same column flips direction; clicking a new column
  // resets direction to a sensible default (date → desc; likes/comments
  // → desc so the operator immediately sees the most-engaged story).
  const onSortClick = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // ── Hide / Unhide handlers ─────────────────────────────────────────
  const beginHide = (j: AppTenantJournal) => {
    setHideTarget(j);
    setHideReasonCode('spam');
    setHideNotes('');
  };
  const beginBulkAction = (action: 'hide' | 'unhide') => {
    setBulkAction(action);
    setHideTarget('bulk');
    setHideReasonCode('spam');
    setHideNotes('');
  };
  const confirmHide = async () => {
    const notes      = hideNotes.trim() || undefined;
    const reasonCode = hideReasonCode;
    if (hideTarget === 'bulk') {
      // Snapshot the bulk subset — operator can't add/remove rows
      // once the run starts. We hide ONLY the currently-published rows
      // when bulkAction === 'hide', and unhide ONLY the hidden rows
      // when 'unhide' — even if the operator's selection mixed both,
      // the verb the operator clicked decides what to do.
      const ids = bulkAction === 'unhide'
        ? selectedByStatus.hidden
        : selectedByStatus.published;
      const verb = bulkAction === 'unhide' ? 'Unhid' : 'Hid';
      // Run sequentially so a single failure (network blip, App fn
      // timeout) doesn't poison the rest of the run. The audit log
      // captures each attempt independently.
      let success = 0;
      let failed  = 0;
      for (const id of ids) {
        const ok = await applyHide(id, bulkAction !== 'unhide', reasonCode, notes);
        if (ok) success++; else failed++;
      }
      if (failed > 0) {
        setError(`${verb} ${success} of ${ids.length} stories — ${failed} failed. Refresh to retry.`);
      }
      setSelectedIds(new Set());
    } else if (hideTarget) {
      await applyHide(hideTarget.id, true, reasonCode, notes);
    }
    setHideTarget(null);
    setHideNotes('');
    setBulkAction(null);
  };
  /** Returns whether the moderation call succeeded. Always clears
   *  busy state. On failure we trigger a hard re-fetch so the UI
   *  doesn't lie about the row's state. */
  const applyHide = async (
    id:    string,
    hide:  boolean,
    code?: ModerationReasonCode,
    notes?: string,
  ): Promise<boolean> => {
    setBusyIds(prev => { const n = new Set(prev); n.add(id); return n; });
    // Look up the row so we can hand the orchestrator the author's
    // email + story title — both are best-effort hints that let the
    // edge fn skip its own enrichment queries.
    const row = journals.find(j => j.id === id);
    const label = code ? reasonMeta(code).label : undefined;
    const r = await moderateTenantJournal({
      tenantId:    tenant.id,
      journalId:   id,
      hide,
      reasonCode:  hide ? code  : undefined,
      reasonLabel: hide ? label : undefined,
      notes:       hide ? notes : undefined,
      authorEmail: row?.author?.email ?? undefined,
      storyTitle:  row?.title ?? undefined,
    });
    if (r.ok) {
      // Optimistic in-place update — avoid a full re-fetch on every
      // toggle. The server response carries the canonical timestamps
      // but we already know what to set them to.
      setJournals(prev => prev.map(j => j.id === id
        ? {
            ...j,
            tenant_hidden_at:          hide ? new Date().toISOString() : null,
            tenant_hidden_reason_code: hide ? (code  ?? null) : null,
            tenant_hidden_notes:       hide ? (notes ?? null) : null,
          }
        : j));
    } else {
      // Hard re-fetch on failure — the optimistic state is now wrong,
      // and reloading is the simplest way to get back to truth.
      await load();
    }
    setBusyIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    return r.ok;
  };

  // ── Sign-in-as ─────────────────────────────────────────────────────
  // Reuses the existing Impersonation plumbing. We default to 'direct'
  // mode (land straight on /home authenticated as the user) because
  // the operator's "I want to see what they see" workflow is faster
  // without the preview hand-off step. Reason is logged in the
  // platform_audit_log via start-impersonation's own audit row.
  const handleSignInAs = async (j: AppTenantJournal) => {
    setSigningInId(j.id);
    try {
      const r = await startImpersonation({
        tenantId: tenant.id,
        userId:   j.user_id,
        reason:   `Stories moderation: viewing story "${j.title || j.id}" as author`,
        mode:     'direct',
      });
      if (!r.ok || !r.redirect_url) {
        setError(r.error || 'Failed to start impersonation.');
        return;
      }
      // New tab so the operator keeps the Stories tab open as their
      // moderation pivot. window.open is blocked by some popup
      // blockers when not triggered by a direct click, but since
      // this fires inside the click handler we should be fine.
      window.open(r.redirect_url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setError(e?.message || 'Failed to start impersonation.');
    } finally {
      setSigningInId(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  // Tenant's public-feed host. Prefer the custom domain (when the
  // operator has a verified one), else fall back to the auto-issued
  // <subdomain>.discoverygem.com. The "Open in public feed" link on
  // each row builds /feed/:id against this host so clicks land on
  // the TENANT's site, not on this Platform UI's domain (which would
  // 404 because /feed lives on the App, not on Platform).
  const tenantHost = tenant.custom_domain
    || (tenant.subdomain ? `${tenant.subdomain}.discoverygem.com` : null);

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Stories</h1>
          <p className="text-sm text-surface-500">
            Journals published to the public feed under {tenant.name}.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-surface-300 text-surface-900 bg-surface-0 hover:bg-surface-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total"     value={stats.total}     icon={<BookOpen className="w-4 h-4" />} delay={0}   />
        <StatCard label="Published" value={stats.published} icon={<Eye      className="w-4 h-4" />} delay={50}  />
        <StatCard label="Drafts"    value={stats.draft}     icon={<BookOpen className="w-4 h-4" />} delay={100} />
        <StatCard label="Hidden"    value={stats.hidden}    icon={<EyeOff   className="w-4 h-4" />} delay={150}
          tooltip="Stories the operator has taken down from the public feed" />
        <StatCard label="Total likes" value={stats.likes}   icon={<Heart    className="w-4 h-4" />} delay={200} />
      </div>

      {/* Unified moderation queue — reports across ALL surfaces
          (Stories / Blueprints / Moments / Journeys), not just the
          journals in the table below. The journal "Reported" chip + the
          per-row modal stay as the journal-specific deep view. */}
      <FlaggedContentPanel tenantId={tenant.id} />

      {/* Search + filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title, author, or destination…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-surface-300 bg-surface-50 text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        {(['all', 'published', 'draft', 'hidden', 'reported'] as StatusFilter[]).map(s => {
          // Reported chip gets a count badge so the operator sees how
          // many open situations need attention without clicking.
          // Rendered rose to match the FeedCard's flag-icon active
          // state — same metaphor of "this needs a response."
          const isReported = s === 'reported';
          const reportedCount = isReported ? stats.reported : 0;
          if (isReported && reportedCount === 0) return null; // hide chip when no reports
          return (
            <button
              key={s}
              type="button"
              onClick={() => {
                setFilter(s);
                if (s !== 'hidden' && s !== 'all') setReasonFilter(null);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filter === s
                  ? isReported
                    ? 'bg-rose-600 text-white'
                    : 'bg-brand-500 text-white'
                  : isReported
                    ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200 hover:text-surface-900'
              }`}
            >
              {isReported && <Flag className="w-3 h-3" />}
              {s[0].toUpperCase()}{s.slice(1)}
              {isReported && (
                <span className={`inline-flex items-center justify-center min-w-[18px] px-1 rounded-full text-[10px] font-bold ${
                  filter === s ? 'bg-white/25' : 'bg-rose-600 text-white'
                }`}>
                  {reportedCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Reason-code sub-filter — only renders when the operator is on
          the All or Hidden tab, because reason codes only mean
          something on hidden rows. Composes with the status filter:
          status=hidden + code=spam shows only the most common
          moderation case. Click the active chip to clear. */}
      {(filter === 'all' || filter === 'hidden') && stats.hidden > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-surface-500 mr-1">
            Hidden reason:
          </span>
          {MODERATION_REASONS.map(r => {
            const active = reasonFilter === r.code;
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => setReasonFilter(active ? null : r.code)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                  active
                    ? `${r.chipBg} ${r.chipText} border-current`
                    : 'bg-surface-50 text-surface-500 border-surface-200 hover:bg-surface-100'
                }`}
                title={r.hint}
              >
                {r.label}
              </button>
            );
          })}
          {reasonFilter && (
            <button
              type="button"
              onClick={() => setReasonFilter(null)}
              className="text-[10px] text-surface-500 hover:text-surface-900 ml-1 underline underline-offset-2"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Bulk-action bar — only renders when at least one row is
          selected. Sits between the filter row and the table so the
          operator sees their selection size while picking the action.
          Both Hide N and Unhide N can appear simultaneously when the
          selection mixes published + hidden rows — each verb counts
          only the rows it applies to. */}
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-brand-500/10 border border-brand-500/30 px-3 py-2">
          <span className="text-sm font-semibold text-brand-700">
            {selectedCount} {selectedCount === 1 ? 'story' : 'stories'} selected
            {selectedByStatus.published.length > 0 && selectedByStatus.hidden.length > 0 && (
              <span className="ml-2 font-normal text-brand-600/80">
                ({selectedByStatus.published.length} live · {selectedByStatus.hidden.length} hidden)
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="px-2.5 py-1 text-xs font-semibold rounded-md border border-surface-300 bg-surface-0 hover:bg-surface-50 text-surface-900 transition-colors"
            >
              Clear
            </button>
            {selectedByStatus.hidden.length > 0 && (
              <button
                type="button"
                onClick={() => beginBulkAction('unhide')}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md bg-brand-500/15 hover:bg-brand-500/25 text-brand-700 border border-brand-500/40 transition-colors"
              >
                <Eye className="w-3 h-3" />
                Unhide {selectedByStatus.hidden.length}
              </button>
            )}
            {selectedByStatus.published.length > 0 && (
              <button
                type="button"
                onClick={() => beginBulkAction('hide')}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md bg-rose-600 hover:bg-rose-700 text-white transition-colors"
              >
                <EyeOff className="w-3 h-3" />
                Hide {selectedByStatus.published.length}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      ) : error ? (
        <div className="rounded-lg border border-rose-300 bg-rose-50 text-rose-700 px-4 py-3 text-sm flex items-center justify-between gap-2">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-rose-500 hover:text-rose-700"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-surface-500">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">
            {journals.length === 0
              ? 'No journals yet — when travelers publish stories they\'ll appear here.'
              : 'No journals match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-surface-200 bg-surface-0">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 text-xs font-bold uppercase tracking-wider text-surface-600">
              <tr>
                {/* Select-all checkbox — applies only to visible
                    published rows. Indeterminate state isn't worth the
                    extra plumbing for this volume, so we just flip
                    between "none of these" and "all of these." */}
                <th className="px-3 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Select all visible published and hidden stories"
                    className="rounded border-surface-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th className="text-left  px-3 py-2.5">
                  <SortHeader label="Story"    sortKey="date"     activeKey={sortKey} dir={sortDir} onClick={onSortClick} />
                </th>
                <th className="text-left  px-3 py-2.5">Author</th>
                <th className="text-left  px-3 py-2.5">Destination</th>
                <th className="text-left  px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">
                  <div className="inline-flex items-center gap-3">
                    <SortHeader label="Likes"    sortKey="likes"    activeKey={sortKey} dir={sortDir} onClick={onSortClick} />
                    <SortHeader label="Comments" sortKey="comments" activeKey={sortKey} dir={sortDir} onClick={onSortClick} />
                  </div>
                </th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              {visible.map(j => {
                const status = statusOf(j);
                const cover  = j.cover_photo_url || j.reel?.thumbnail_url || j.photos[0] || null;
                const busy   = busyIds.has(j.id);
                const checked  = selectedIds.has(j.id);
                // Phase 3A — per-row report summary lookup. Returns
                // undefined when the row has no open reports, which
                // means the report column stays blank and the
                // "Needs review" border doesn't render.
                const reports = reportsByJournal.get(j.id);
                const reportCount = reports?.open_count ?? 0;
                const needsReview = reportCount >= NEEDS_REVIEW_THRESHOLD;
                // Author signals — pull from the precomputed map and
                // only render the pill when there's something
                // meaningful to say (i.e. at least one hidden story
                // OR a decent volume of published ones; pure first-
                // timers don't get the pill so we don't clutter
                // every row).
                const aStats = authorStats.get(j.user_id);
                const showAuthorPill = !!aStats && (aStats.hidden > 0 || aStats.published >= 5);
                // Both published and hidden rows are selectable —
                // published feed Hide, hidden feed Unhide. Drafts have
                // no public visibility to flip, so their checkbox stays
                // disabled.
                const isSelectable = status !== 'draft';
                // Build the absolute URL to the public feed on the
                // TENANT's site (not the Platform's). Without this,
                // clicking the external-link icon would route to
                // /feed/:id on the Platform domain, which 404s
                // because /feed only exists on the App.
                const publicHref = tenantHost && j.published_at
                  ? `https://${tenantHost}/feed/${j.id}`
                  : null;
                return (
                  <tr
                    key={j.id}
                    className={`hover:bg-surface-50 transition-colors ${
                      checked ? 'bg-brand-500/5' : ''
                    } ${
                      // Phase 3A — 3+ open reports tint the row's left
                      // edge so it visibly stands out as "Needs review"
                      // even when the operator hasn't filtered to the
                      // Reported tab. We use border-l (not full border)
                      // so it doesn't fight with the table's divide-y.
                      needsReview ? 'border-l-4 border-rose-500 bg-rose-50/30' : ''
                    }`}>
                    {/* Row checkbox — only enabled for published rows
                        (drafts have nothing to hide; hidden rows would
                        be a no-op). Disabled cells stay rendered so the
                        column doesn't shift width as filters change. */}
                    <td className="px-3 py-2.5 w-8">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(j.id)}
                        disabled={!isSelectable}
                        aria-label={`Select ${j.title || 'story'}`}
                        className="rounded border-surface-300 text-brand-600 focus:ring-brand-500 disabled:opacity-30"
                      />
                    </td>
                    {/* Story cell — cover + title + day */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="relative w-12 h-12 rounded-md overflow-hidden bg-surface-100 flex-shrink-0">
                          {cover ? (
                            <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-4 h-4 text-surface-400" />
                            </div>
                          )}
                          {j.reel && (
                            <span className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-black/70">
                              <Play className="w-2.5 h-2.5 text-white fill-white" />
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-surface-900 truncate">
                            {j.title || 'Untitled story'}
                          </p>
                          {/* Freshness footer — branches on status so
                              the operator immediately sees the most
                              relevant date plus a "live N days"
                              counter for currently-public rows. */}
                          <StoryFreshness j={j} status={status} />
                        </div>
                      </div>
                    </td>
                    {/* Author cell — two lines now: display name on top,
                        email beneath in muted small text. The operator
                        can click the email (mailto:) to reach out about
                        a moderation decision without leaving the page.
                        max-w widened from 160 to 220 to fit typical
                        emails without truncating mid-domain. */}
                    <td className="px-3 py-2.5 truncate max-w-[220px]">
                      <div className="min-w-0">
                        <p className="text-surface-900 truncate">
                          {j.author.display_name}
                        </p>
                        {j.author.email && (
                          <a
                            href={`mailto:${j.author.email}`}
                            className="text-[11px] text-surface-500 hover:text-brand-600 truncate block transition-colors"
                            title={`Email ${j.author.display_name}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {j.author.email}
                          </a>
                        )}
                        {/* Phase 3A author signals — quick "hidden /
                            published" ratio so repeat-offender
                            patterns surface at a glance. Hidden when
                            the author has nothing notable on either
                            axis to keep the column from getting
                            crowded for first-time authors. */}
                        {showAuthorPill && aStats && (
                          <p className={`text-[10px] mt-0.5 inline-flex items-center gap-1 ${
                            aStats.hidden > 0 ? 'text-rose-600' : 'text-surface-500'
                          }`}
                            title="Total stories from this author"
                          >
                            {aStats.hidden > 0 && <EyeOff className="w-2.5 h-2.5" />}
                            {aStats.hidden} hidden · {aStats.published} published
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-surface-600">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3 opacity-60" />
                        {j.destinations[0] || '—'}
                        {j.destinations.length > 1 && (
                          <span className="text-[10px] opacity-60">+{j.destinations.length - 1}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col items-start gap-1">
                        <StatusChip
                          status={status}
                          reasonCode={j.tenant_hidden_reason_code}
                          notes={j.tenant_hidden_notes}
                        />
                        {/* Reports badge — only renders when ≥1 OPEN
                            report. Acts as the open-reports modal
                            trigger. Color escalates to a brighter
                            rose at the "Needs review" threshold so
                            severity is visible at a glance. */}
                        {reportCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setReportsModalJournal(j)}
                            title={`${reportCount} open report${reportCount === 1 ? '' : 's'} — click to view`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors border ${
                              needsReview
                                ? 'bg-rose-600 text-white border-rose-700 hover:bg-rose-700'
                                : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                            }`}
                          >
                            <Flag className="w-2.5 h-2.5" />
                            {reportCount}
                            {needsReview && (
                              <span className="font-extrabold">· Needs review</span>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-3 text-xs text-surface-600">
                        <span className="inline-flex items-center gap-1" title="Likes">
                          <Heart className="w-3 h-3" />
                          <span className="tabular-nums">{j.feed_likes_count}</span>
                        </span>
                        <span className="inline-flex items-center gap-1" title="Comments">
                          <MessageCircle className="w-3 h-3" />
                          <span className="tabular-nums">{j.feed_comments_count}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {/* Sign in as this user — reuses the existing
                            Impersonation plumbing so the operator can
                            see exactly what the author sees in a single
                            click. Always available regardless of
                            publish/hide state because the operator
                            often wants to "see as them" precisely while
                            triaging a hidden story. */}
                        <button
                          type="button"
                          onClick={() => handleSignInAs(j)}
                          disabled={signingInId === j.id}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-surface-100 text-surface-500 hover:text-brand-600 disabled:opacity-50 transition-colors"
                          title={`Sign in as ${j.author.display_name}`}
                        >
                          {signingInId === j.id
                            ? <Spinner size="sm" />
                            : <LogIn className="w-3.5 h-3.5" />}
                        </button>
                        {/* Open in the TENANT's public feed — absolute
                            URL built from custom_domain or
                            <subdomain>.discoverygem.com. Only shown
                            for published rows (drafts have no public
                            URL yet) and only when we actually have a
                            host to point at. */}
                        {publicHref && (
                          <a
                            href={publicHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-surface-100 text-surface-500 hover:text-surface-900 transition-colors"
                            title={`Open on ${tenantHost}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {/* Hide/Unhide — only meaningful for non-draft
                            rows. Drafts aren't visible to the public
                            yet, so there's nothing to hide. */}
                        {status === 'published' && (
                          <button
                            type="button"
                            onClick={() => beginHide(j)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border border-surface-300 bg-surface-0 hover:bg-surface-50 text-surface-900 disabled:opacity-50 transition-colors"
                            title="Hide from public feed"
                          >
                            <EyeOff className="w-3 h-3" />
                            Hide
                          </button>
                        )}
                        {status === 'hidden' && (
                          <button
                            type="button"
                            onClick={() => applyHide(j.id, false)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border border-brand-500/40 bg-brand-500/10 hover:bg-brand-500/20 text-brand-700 disabled:opacity-50 transition-colors"
                            title="Restore to public feed"
                          >
                            <Eye className="w-3 h-3" />
                            Unhide
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Phase 3A — Reports modal. Surfaces the per-journal report
          list + Dismiss action. Reads from the App side on open so
          dismissed reports + history land in one place. */}
      {reportsModalJournal && (
        <ReportsModal
          tenantId={tenant.id}
          journal={reportsModalJournal}
          onClose={() => setReportsModalJournal(null)}
          onAllDismissed={() => {
            // Refresh the summary map so the row's badge clears
            // without a full page reload.
            setReportsByJournal(prev => {
              const next = new Map(prev);
              next.delete(reportsModalJournal.id);
              return next;
            });
            setReportsModalJournal(null);
          }}
        />
      )}

      {/* Hide-reason modal — shared between single-row and bulk hide.
          When hideTarget === 'bulk' the body shows the selection count
          rather than a specific story title. The reason field applies
          uniformly to every row in the bulk run. */}
      {hideTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHideTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-surface-0 border border-surface-200 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <h3 className="text-base font-bold text-surface-900">
                  {hideTarget === 'bulk'
                    ? bulkAction === 'unhide'
                      ? `Restore ${selectedByStatus.hidden.length} stories`
                      : `Hide ${selectedByStatus.published.length} stories`
                    : 'Hide this story'}
                </h3>
                <p className="text-xs text-surface-500 mt-0.5">
                  {hideTarget === 'bulk'
                    ? bulkAction === 'unhide'
                      ? 'Each story will reappear in the public feed immediately. Authors aren\'t notified about un-hiding.'
                      : 'Each story will disappear from the public feed immediately. Authors keep their drafts in their journal.'
                    : 'It will disappear from the public feed immediately. The author keeps the journal in their own hub.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHideTarget(null)}
                className="text-surface-500 hover:text-surface-900 p-1"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="rounded-md bg-surface-100 px-3 py-2 mb-3">
              {hideTarget === 'bulk' ? (
                <p className="text-sm font-semibold text-surface-900">
                  {bulkAction === 'unhide' ? selectedByStatus.hidden.length : selectedByStatus.published.length}{' '}
                  {(bulkAction === 'unhide' ? selectedByStatus.hidden.length : selectedByStatus.published.length) === 1 ? 'story' : 'stories'}{' '}
                  from the current selection
                </p>
              ) : (
                <>
                  <p className="text-sm font-semibold text-surface-900 truncate">
                    {hideTarget.title || 'Untitled story'}
                  </p>
                  <p className="text-xs text-surface-500">
                    by {hideTarget.author.display_name}
                  </p>
                </>
              )}
            </div>
            {/* Reason picker + notes only makes sense when hiding —
                the unhide flow has no companion email and clears the
                fields server-side, so showing them here would be
                pointless ceremony. Phase 3C split the old freeform
                reason into a canonical category dropdown (required-ish:
                always has a value because the picker has no empty
                option) + optional free-text notes. The category drives
                the Dashboard trend chart; the notes are the operator's
                personal explanation that gets emailed to the author. */}
            {!(hideTarget === 'bulk' && bulkAction === 'unhide') && (
              <>
                <label className="block text-xs font-semibold text-surface-900 mb-1">
                  Category
                </label>
                <select
                  value={hideReasonCode}
                  onChange={e => setHideReasonCode(e.target.value as ModerationReasonCode)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-surface-300 bg-surface-50 text-surface-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent mb-1"
                >
                  {MODERATION_REASONS.map(r => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-surface-500 mb-3">
                  {reasonMeta(hideReasonCode).hint}
                </p>
                <label className="block text-xs font-semibold text-surface-900 mb-1">
                  Notes (optional — sent to the author{hideTarget === 'bulk' ? 's' : ''})
                </label>
                <textarea
                  value={hideNotes}
                  onChange={e => setHideNotes(e.target.value.slice(0, 500))}
                  placeholder="Add any details the author should know — e.g. which part of the story triggered the action."
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-md border border-surface-300 bg-surface-50 text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                />
                <p className="text-[10px] text-surface-400 text-right mt-1">
                  {hideNotes.length}/500
                </p>
              </>
            )}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setHideTarget(null); setBulkAction(null); }}
                className="px-3 py-1.5 text-sm font-semibold rounded-md border border-surface-300 bg-surface-0 hover:bg-surface-50 text-surface-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmHide}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md text-white transition-colors ${
                  bulkAction === 'unhide'
                    ? 'bg-brand-600 hover:bg-brand-700'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {hideTarget === 'bulk'
                  ? bulkAction === 'unhide'
                    ? `Unhide ${selectedByStatus.hidden.length}`
                    : `Hide ${selectedByStatus.published.length}`
                  : 'Hide story'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sortable column header — a tiny button-like th content that
// shows a directional chevron when this is the active sort key, and
// the neutral ArrowUpDown otherwise. Kept inline so we don't have to
// thread classNames through a sub-component for every column. ────────
function SortHeader({
  label, sortKey, activeKey, dir, onClick,
}: {
  label:     string;
  sortKey:   SortKey;
  activeKey: SortKey;
  dir:       SortDir;
  onClick:   (k: SortKey) => void;
}) {
  const isActive = activeKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={`inline-flex items-center gap-1 transition-colors ${
        isActive ? 'text-surface-900' : 'text-surface-600 hover:text-surface-900'
      }`}
      title={`Sort by ${label.toLowerCase()}`}
    >
      <span>{label}</span>
      {isActive
        ? (dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
        : <ArrowUpDown className="w-3 h-3 opacity-50" />}
    </button>
  );
}

// ── Story freshness footer — second line under the title in the
// Story cell. Branches on status so the operator sees the most
// relevant timestamp + a "live N days" counter for currently-public
// rows. Drafts show "created N ago" instead; hidden rows show when
// they were taken down. "Edited" appears only when the author's
// edit gap from publish exceeds 2 days (filters out hide-trigger
// noise on updated_at).
function StoryFreshness({
  j, status,
}: { j: AppTenantJournal; status: Exclude<StatusFilter, 'all'> }) {
  const dayLabel = j.day_number != null ? `Day ${j.day_number}` : 'Trip-wide';

  if (status === 'draft') {
    const days = daysSince(j.created_at);
    return (
      <p className="text-[11px] text-surface-500">
        {dayLabel} · Draft · created {shortDate(j.created_at)}
        {days != null && ` (${days}d ago)`}
      </p>
    );
  }

  if (status === 'hidden') {
    return (
      <p className="text-[11px] text-surface-500">
        {dayLabel} · Published {shortDate(j.published_at)}
        {j.tenant_hidden_at && ` · Hidden ${shortDate(j.tenant_hidden_at)}`}
      </p>
    );
  }

  // status === 'published' — currently live on the public feed.
  const liveDays = daysSince(j.published_at);
  const showEdited = meaningfullyEdited(j);
  return (
    <p className="text-[11px] text-surface-500">
      {dayLabel} · Published {shortDate(j.published_at)}
      {showEdited && ` · edited ${shortDate(j.updated_at)}`}
      {liveDays != null && (
        <>
          {' · '}
          <span className="font-semibold text-emerald-700">
            Live {liveDays}d
          </span>
        </>
      )}
    </p>
  );
}

// ── Reports modal (Phase 3A) ─────────────────────────────────────────
// Surfaces per-journal report history. Lazily fetches the full list on
// mount (open reports + dismissed history together) so the operator
// can see prior decisions alongside the current queue. Dismiss button
// resolves every OPEN report on the journal — the parent then prunes
// the summary map so the row's badge clears.
function ReportsModal({
  tenantId, journal, onClose, onAllDismissed,
}: {
  tenantId:       string;
  journal:        AppTenantJournal;
  onClose:        () => void;
  onAllDismissed: () => void;
}) {
  const [reports, setReports]   = useState<TenantReportDetail[] | null>(null);
  const [loading, setLoading]   = useState(true);
  const [busy,    setBusy]      = useState(false);
  const [reason,  setReason]    = useState('');
  const [error,   setErrorMsg]  = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchJournalReports(tenantId, journal.id)
      .then(r => { if (!cancelled) setReports(r); })
      .catch(() => { if (!cancelled) setReports([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, journal.id]);

  const open      = (reports ?? []).filter(r => !r.dismissed_at);
  const resolved  = (reports ?? []).filter(r =>  r.dismissed_at);

  const handleDismissAll = async () => {
    if (open.length === 0) return;
    setBusy(true);
    setErrorMsg(null);
    const r = await dismissJournalReports({
      tenantId,
      journalId: journal.id,
      reason: reason.trim() || undefined,
    });
    if (!r.ok) {
      setErrorMsg(r.error || 'Dismiss failed');
      setBusy(false);
      return;
    }
    // Parent updates the summary map + closes the modal.
    onAllDismissed();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl bg-surface-0 border border-surface-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface-0 border-b border-surface-200 px-5 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-surface-900 flex items-center gap-2">
              <Flag className="w-4 h-4 text-rose-600" />
              Reader reports
            </h3>
            <p className="text-xs text-surface-500 mt-0.5 truncate">
              {journal.title || 'Untitled story'} · by {journal.author.display_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-surface-500 hover:text-surface-900 p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Spinner /></div>
          ) : open.length === 0 && resolved.length === 0 ? (
            <p className="text-sm text-surface-500 text-center py-6">
              No reports on this story.
            </p>
          ) : (
            <>
              {/* Open reports — primary focus of the modal */}
              {open.length > 0 && (
                <section>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-surface-500 mb-2">
                    Open · {open.length}
                  </h4>
                  <ul className="space-y-2">
                    {open.map(r => (
                      <ReportRow key={r.id} report={r} />
                    ))}
                  </ul>
                </section>
              )}

              {/* Resolved history — collapsible would be nice but at
                  this volume a flat list under a divider is plenty */}
              {resolved.length > 0 && (
                <section className="pt-3 border-t border-surface-200">
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-surface-500 mb-2">
                    Previously dismissed · {resolved.length}
                  </h4>
                  <ul className="space-y-2 opacity-70">
                    {resolved.map(r => (
                      <ReportRow key={r.id} report={r} />
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {/* Dismiss block — only renders when there's something to
              dismiss. Reason is optional and stored on each row for
              audit trail. */}
          {open.length > 0 && (
            <div className="pt-3 border-t border-surface-200 space-y-2">
              <label className="block text-xs font-semibold text-surface-900">
                Dismissal note (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 200))}
                rows={2}
                placeholder="e.g. reviewed, not actionable"
                className="w-full px-3 py-2 text-sm rounded-md border border-surface-300 bg-surface-50 text-surface-900 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              />
              {error && (
                <p className="text-[11px] text-rose-600">{error}</p>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md border border-surface-300 bg-surface-0 hover:bg-surface-50 text-surface-900"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleDismissAll}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-60"
                >
                  {busy && <Spinner size="sm" />}
                  Dismiss {open.length}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Single report row inside the modal. Renders reason category chip
 *  using the canonical taxonomy palette + reporter display + notes if
 *  any. Resolved rows additionally show the dismissed_reason. */
function ReportRow({ report }: { report: TenantReportDetail }) {
  const meta = reasonMeta(report.reason_code);
  return (
    <li className="rounded-lg border border-surface-200 px-3 py-2 bg-surface-50/40">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.chipBg} ${meta.chipText}`}>
              {meta.label}
            </span>
            <span className="text-[11px] text-surface-500">
              · by {report.reporter.display_name}
            </span>
          </div>
          {report.notes && (
            <p className="text-[12px] text-surface-700 mt-1 italic">
              "{report.notes}"
            </p>
          )}
          {report.dismissed_at && report.dismissed_reason && (
            <p className="text-[10px] text-surface-500 mt-1">
              Dismissed: {report.dismissed_reason}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-surface-500 tabular-nums">
            {shortDate(report.created_at)}
          </p>
          {report.reporter.email && (
            <a
              href={`mailto:${report.reporter.email}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] text-surface-500 hover:text-brand-600 transition-colors"
            >
              {report.reporter.email}
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Status chip — three states (published, draft, hidden) ────────────
// Hidden rows additionally render the canonical reason category as a
// second smaller chip beneath the status, with the operator's notes
// surfaced via the title tooltip. This lets the operator skim the
// table and identify "what kind of problem is this" at a glance
// without expanding anything.
function StatusChip({
  status, reasonCode, notes,
}: {
  status:     Exclude<StatusFilter, 'all'>;
  reasonCode: string | null;
  notes:      string | null;
}) {
  if (status === 'published') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
        <Eye className="w-2.5 h-2.5" />
        Published
      </span>
    );
  }
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-surface-100 text-surface-600 border border-surface-200">
        Draft
      </span>
    );
  }
  // Hidden — main amber chip + reason sub-chip (only when we have a
  // code). Pre-3C historical rows that lack a code show just the main
  // chip; the freeform note text still surfaces in the tooltip so
  // nothing is lost.
  const meta = reasonCode ? reasonMeta(reasonCode) : null;
  // Tooltip combines BOTH category label and notes so a hover gives
  // the operator everything without a click. notes from pre-3C rows
  // contain the legacy freeform text by virtue of the rename.
  const tooltip = [
    meta ? `Category: ${meta.label}` : null,
    notes ? `Notes: ${notes}` : null,
  ].filter(Boolean).join(' · ') || 'Hidden from public feed';
  return (
    <div className="inline-flex flex-col items-start gap-0.5" title={tooltip}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
        <EyeOff className="w-2.5 h-2.5" />
        Hidden
      </span>
      {meta && (
        <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold ${meta.chipBg} ${meta.chipText}`}>
          {meta.label}
        </span>
      )}
    </div>
  );
}
